import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { getAttachmentUrl } from '../../api/news';
import { createMarkdownComponents } from '../common/MarkdownComponents';

// Simplified Markdown preview component with truncation
const MarkdownPreview = ({ content, maxLength = 300 }) => {
  const isDarkMode = document.documentElement.classList.contains('dark');
  let truncated = content;
  const shouldTruncate = content.length > maxLength;
  if (shouldTruncate) {
    truncated = content.substring(0, maxLength) + '...';
  }
  return (
    <div className="prose prose-sm dark:prose-invert">
      <ReactMarkdown
        components={createMarkdownComponents(isDarkMode)}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {truncated}
      </ReactMarkdown>
      {shouldTruncate && <p className="text-blue-600">Read more</p>}
    </div>
  );
};

// Component for displaying attachments
const Attachments = ({ attachments, postId }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2">
      <p className="text-sm font-semibold mb-2">Attachments</p>
      {attachments.map(attachment => (
        <div key={attachment.id} className="flex items-center mb-1">
          {/* Simple inline SVG icon for attachment */}
          <svg
            className="w-4 h-4 mr-1 text-gray-600 dark:text-gray-300"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M8.5 2a4.5 4.5 0 00-4.5 4.5v7a3.5 3.5 0 007 0V6a2.5 2.5 0 00-5 0v7a.5.5 0 001 0v-7a1.5 1.5 0 013 0v7a2.5 2.5 0 01-5 0v-7A4.5 4.5 0 008.5 2z" />
          </svg>
          <a
            href={getAttachmentUrl(postId, attachment.filename)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {attachment.filename}
          </a>
        </div>
      ))}
    </div>
  );
};

const NewsItem = ({
  post,
  isPreview = true,
  canEdit = false,
  onEdit,
  onDelete
}) => {
  const isDarkMode = document.documentElement.classList.contains('dark');
  const formattedDate = format(new Date(post.published_at), 'MMM d, yyyy');
  
  return (
    <div
      className={`mb-4 p-4 rounded-lg shadow-md relative border ${
        post.is_pinned ? 'border-blue-600' : 'border-gray-300'
      }`}
    >
      {post.is_pinned && (
        <svg
          className="absolute top-2 right-2 w-5 h-5 text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M4 13V7a4 4 0 018 0v6a4 4 0 01-8 0z" />
          <path
            fillRule="evenodd"
            d="M6 3a2 2 0 00-2 2v1h10V5a2 2 0 00-2-2H6z"
            clipRule="evenodd"
          />
        </svg>
      )}
      
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {post.title}
        </h2>
        
        <div className="flex items-center mb-2">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {post.author_name ? `Posted by ${post.author_name} • ` : ''}
            {formattedDate}
          </p>
        </div>
        
        <div className="mb-2">
          {post.roles.map(role => (
            <span
              key={role.id}
              className="inline-block px-2 py-1 mr-2 mb-2 text-xs font-medium rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
            >
              {role.name}
            </span>
          ))}
        </div>
        
        <hr className="my-2 border-gray-300 dark:border-gray-600" />
        
        {isPreview ? (
          <MarkdownPreview content={post.content} />
        ) : (
          <div className="prose prose-sm sm:prose lg:prose-lg dark:prose-invert">
            <ReactMarkdown
              components={createMarkdownComponents(isDarkMode)}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {post.content}
            </ReactMarkdown>
          </div>
        )}
        
        {!isPreview && <Attachments attachments={post.attachments} postId={post.id} />}
      </div>
      
      <div className="flex justify-between items-center mt-4">
        {isPreview && (
          <RouterLink to={`/news/${post.id}`} className="no-underline">
            <button className="px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-600 hover:text-white transition-colors">
              Read More
            </button>
          </RouterLink>
        )}
        
        {canEdit && (
          <div className="flex space-x-2">
            <button
              onClick={() => onEdit(post)}
              className="p-2 text-blue-600 hover:text-blue-800"
              title="Edit Post"
            >
              <svg
                className="w-5 h-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11 4h6a2 2 0 012 2v6m-3 5l-7 7H4a2 2 0 01-2-2v-4l7-7"
                />
              </svg>
            </button>
            <button
              onClick={() => onDelete(post)}
              className="p-2 text-red-600 hover:text-red-800"
              title="Delete Post"
            >
              <svg
                className="w-5 h-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsItem;